/**
 * English translation strings for BetMeBro
 */
export const en = {
  // Brand identity
  brand: {
    name: "BetMeBro",
    tagline: "Predict. Bet. Win.",
    logoLabel: "BetMeBro Logo",
  },

  // Common UI elements
  common: {
    loading: "Loading...",
    close: "Close",
    copied: "Copied!",
    retry: "Retry",
    tryAgain: "Try Again",
  },

  // Navigation
  nav: {
    markets: "Markets",
    worldCup: "World Cup",
    profile: "Profile",
    leaderboard: "Leaderboard",
    createMarket: "Create market",
    admin: "Admin",
  },

  // Admin access gate (RBAC, 2026-06-12)
  admin: {
    exit: "Exit Admin",
    access: {
      title: "Admin Access",
      connectPrompt: "Connect your wallet to access the admin console.",
      denied:
        "This wallet has no admin role. Ask an admin to grant operator access.",
    },
  },

  // Authentication and wallet
  auth: {
    connectWallet: "Connect Wallet",
    walletDisconnected: "Wallet Disconnected",
    walletRequired: "Wallet connection required",
    loggedInAs: "Logged in as {{address}}. Click to open wallet menu",
    connectWalletToSignIn: "Connect Solana wallet to sign in",
    logoutFromWallet: "Logout from wallet",
    logout: "Logout",
    reconnectWallet: "Reconnect Wallet",
    reconnectWalletDesc:
      "Your wallet has been disconnected. Please reconnect to continue.",
    connectAndSignIn: "Connect & Sign In",
  },

  // Wallet connection
  wallet: {
    connecting: "Connecting...",
    connectWallet: "Connect Wallet",
    copyAddress: "Copy Address",
    viewOnExplorer: "View on Explorer",
    disconnect: "Disconnect",
    balance: "Balance",
    refresh: "Refresh",
    send: "Send",
    transfer: "Transfer",
    rename: {
      edit: "Rename wallet",
      placeholder: "Wallet name",
      failed: "Couldn't rename wallet",
    },
    receive: {
      menuItem: "Receive",
      title: "Receive Funds",
      scanHint: "Scan this code or copy the address to receive funds.",
      addressLabel: "Your wallet address",
      qrAlt: "QR code for your wallet address",
      copy: "Copy address",
      copied: "Address copied to clipboard",
      copyFailed: "Couldn't copy — select the address and copy it manually.",
      done: "Done",
    },
    selectToken: "Select Token",
    recipientAddress: "Recipient Address",
    pasteAddress: "Paste",
    amount: "Amount",
    available: "Available",
    maxAmount: "Max",
    invalidAddress: "Invalid wallet address",
    selfTransferError: "Cannot transfer to yourself",
    insufficientBalance: "Insufficient balance",
    solReserveHint: "A small SOL reserve is kept for transaction fees",
    confirmInWallet: "Confirm in wallet...",
    transferSuccess: "Transfer successful!",
    transferSubmitted: "Submitted — finalising on-chain",
    transferSubmittedDesc:
      "Your transaction has been broadcast. It will appear in your history once the cluster confirms it (usually within a minute).",
    transferSubmittedToast:
      "Transaction submitted — it will finalise on-chain shortly.",
    done: "Done",
    tryAgain: "Try Again",

    modal: {
      secureBadge: "Secure",
      protectedFooter: "Protected by BetMeBro",
      titles: {
        select: "Connect Wallet",
        create: "Create New Wallet",
        import: "Import Wallet",
        ledger: "Connect Ledger",
        mnemonic: "Backup Recovery Phrase",
        backup: "Backup Your Wallet",
        default: "Wallet",
      },
      descriptions: {
        select: "Choose how you want to connect",
        create: "Create a new wallet secured by biometric authentication",
        import:
          "Import an existing wallet using your recovery phrase or private key",
        ledger: "Connect your Ledger hardware wallet",
        mnemonic: "Write down these words in order and keep them safe",
        backup: "Download a backup of your encrypted wallet",
      },
    },

    select: {
      yourWallets: "Your Wallets",
      searchPlaceholder: "Search wallets...",
      backupBadge: "Backup",
      noMatch: 'No wallets match "{{query}}"',
      maxLocalReached: "Maximum {{max}} local wallets reached.",
      maxLedgerReached: "Maximum {{max}} Ledger wallets reached.",
      getStarted: "Get Started",
      hardwareWalletHeading: "Hardware Wallet",
      walletFallbackName: "Wallet {{prefix}}",
      create: {
        title: "Create New Wallet",
        desc: "Secured with TouchID/FaceID",
      },
      import: {
        title: "Import Existing Wallet",
        desc: "From recovery phrase or private key",
      },
      ledger: {
        title: "Connect Ledger",
        desc: "Use your hardware wallet",
      },
      prfUnsupported:
        "WebAuthn PRF not supported. Use Chrome or Edge with a compatible authenticator.",
      webHidUnsupported:
        "WebHID not supported. Use Chrome or Edge to connect Ledger.",
    },

    create: {
      back: "Back",
      nameLabel: "Wallet Name (optional)",
      namePlaceholder: "My Wallet",
      twoStepTitle: "Two-Step Verification",
      twoStepIntro: "For maximum security, you'll verify your identity twice:",
      twoStepStep1: "Create your secure credential",
      twoStepStep2: "Encrypt your wallet keys",
      biometricTitle: "Biometric Authentication",
      biometricDesc:
        "Your wallet will be protected by TouchID, FaceID, or Windows Hello. You'll need to authenticate each time you sign a transaction.",
      submit: "Create with Biometrics",
      progress: {
        stepOf: "Step {{current}} of {{total}}",
        step1Title: "Creating Secure Credential",
        step1Desc: "Verify your identity with Face ID or Touch ID",
        step2Title: "Encrypting Your Wallet",
        step2Desc: "Verify again to complete the secure setup",
      },
      mnemonic: {
        warningLabel: "Important:",
        warningBody:
          "Write down these 24 words in order. This is the only way to recover your wallet if you lose access to this device. Never share these words with anyone.",
        copy: "Copy Words",
        copied: "Copied!",
        hint: "You'll verify your backup on the next screen",
        continue: "Continue to Verify",
      },
      verify: {
        title: "Verify Your Backup",
        desc: "Enter the following words from your recovery phrase to confirm you've saved it correctly.",
        wordLabel: "Word #{{n}}",
        wordPlaceholder: "Enter word #{{n}}",
        back: "Back",
        submit: "Verify & Complete",
      },
    },

    mnemonicDisplay: {
      empty: "No mnemonic available",
      warningTitle: "Write Down These Words",
      warningDesc:
        "This is your wallet recovery phrase. Write it down on paper and store it in a secure location. Never share it with anyone.",
      copy: "Copy to Clipboard",
      copied: "Copied!",
      confirmLabel:
        "I have written down my recovery phrase and stored it safely",
      done: "Done",
      markBackedUpFailed:
        "Couldn't save your backup confirmation. You can confirm again from the backup banner.",
    },

    backup: {
      warningTitle: "Back Up Your Wallet",
      warningDesc:
        "Download an encrypted backup of your wallet. You'll need your biometric authentication on this device to restore it.",
      infoIntro:
        "Your wallet backup is encrypted with a key derived from your biometric authentication (TouchID/FaceID). This means:",
      info1: "The backup can only be restored on this device",
      info2: "You'll need your biometric to decrypt it",
      info3:
        "If you lose access to this device, use your recovery phrase instead",
      download: "Download Encrypted Backup",
      downloadFailed: "Couldn't download the backup file. Please try again.",
      skip: "Skip for now",
      laterReminder:
        "You can always download a backup later from wallet settings",
      bannerLabel: "Wallet not backed up",
      bannerCta: "Backup Now",
    },

    import: {
      back: "Back",
      methods: {
        mnemonic: "Recovery Phrase",
        privateKey: "Private Key",
        backup: "Backup File",
      },
      nameLabel: "Wallet Name (optional)",
      namePlaceholder: "Imported Wallet",
      backupFileLabel: "Backup File",
      backupFileSelect: "Click to select backup file",
      backupFileChange: "Click to change",
      backupFileWarning:
        "⚠️ Only works on the same device where backup was created",
      mnemonicLabel: "Recovery Phrase",
      privateKeyLabel: "Private Key",
      mnemonicPlaceholder: "Enter your 12 or 24 word recovery phrase...",
      privateKeyPlaceholder: "Enter your base58 encoded private key...",
      mnemonicHint: "Enter words separated by spaces",
      privateKeyHint: "Base58 encoded 64-byte private key",
      backupBoxTitle: "Restore from Backup",
      backupBoxDesc:
        "The backup file contains your encrypted wallet. You'll need to authenticate with biometrics after restoring.",
      secureBoxTitle: "Secure Import",
      secureBoxDesc:
        "Your wallet will be encrypted with biometric authentication. The original phrase/key is never stored.",
      submitBackup: "Restore from Backup",
      submitImport: "Import with Biometrics",
      restoring: "Restoring...",
      importing: "Importing...",
      errors: {
        selectBackupFile: "Please select a backup file",
        enterValue: "Please enter your recovery phrase or private key",
      },
    },

    ledger: {
      back: "Back",
      step1Title: "Connect your Ledger",
      step1Desc: "Plug in your Ledger device and unlock it with your PIN",
      step2Title: "Open Solana App",
      step2Desc: "Navigate to the Solana app on your Ledger and open it",
      step3Title: "Click Connect",
      step3Desc: "Click the button below and select your Ledger device",
      pathLabel: "Derivation Path",
      pathDefault: "Default ({{path}})",
      pathLive: "Ledger Live ({{path}})",
      pathAccount: "Account {{n}} ({{path}})",
      pathHint: "Most users should use the default path",
      submit: "Connect Ledger",
      connectingTitle: "Connecting to Ledger...",
      connectingDesc:
        "Select your device in the browser popup and confirm on your Ledger",
      blindWarningTitle: "Enable Blind Signing",
      blindWarningDesc:
        "Some transactions may require blind signing to be enabled in your Ledger Solana app settings.",
    },

    compat: {
      title: "Wallet Compatibility Notice",
      desc: "This application is running in an embedded context, which may limit wallet connectivity. Some wallets (like Trust Wallet) cannot connect in this mode due to security restrictions.",
      openNewTab: "Open in New Tab",
      dismiss: "Dismiss",
      recommended: "Recommended wallets: Phantom, Solflare, or Coinbase Wallet",
      inlineTitle: "Limited wallet support detected",
      inlineDescPrefix:
        "Some wallets may not connect properly. For best results, ",
      inlineDescLink: "open in a new tab",
      inlineDescSuffix: ".",
    },

    info: {
      noWallet: "No wallet connected",
      addressCopiedTitle: "Address Copied",
      addressCopiedDesc: "Wallet address copied to clipboard",
      copyAddressAria: "Copy address",
      viewOnExplorerAria: "View on explorer",
      solBalance: "SOL Balance",
      tokenHoldings: "Token Holdings",
      tokenCount_one: "{{count}} token",
      tokenCount_other: "{{count}} tokens",
      tokenAmount: "{{amount}} tokens",
      noTokens: "No tokens found",
      viewTokenAria: "View token",
    },

    txConfirm: {
      defaultTitle: "Confirm transaction",
      hint: "The wallet will prompt for a fingerprint or hardware-key approval after you confirm. Cancel here to stop before any signature is requested.",
      cancel: "Cancel",
      confirm: "Confirm & sign",
      titles: {
        placeBet: "Place Bet",
        // Phase F (2026-05-18, economic-model-v2 G5 merged): single
        // settle_bet ix replaces claim_winnings. Winner path = claim
        // winnings; loser path = close Bet PDA + recover rent. Title
        // remains user-friendly "Settle Bet" — UX details (winner-only
        // payout vs loser-only rent recovery) are toast-side.
        settleBet: "Settle Bet",
        reclaimBet: "Reclaim Stake",
        sendSol: "Send SOL",
        sendToken: "Send Token",
        createMarket: "Create Market",
        closeMarket: "Close Market",
        drainOracleReq: "Reclaim Oracle Rent",
      },
      labels: {
        market: "Market",
        amount: "Amount",
        wallet: "Wallet",
        to: "To",
        from: "From",
        question: "Question",
        resolvesAt: "Resolves at",
        creationFee: "Creation fee",
        proposerReward: "Oracle proposer reward",
        totalCost: "Total cost",
        totalPayout: "Total potential payout",
      },
      sides: {
        yes: "YES",
        no: "NO",
      },
    },

    recipientPlaceholder: "So1ana...",
  },

  // Error messages
  errors: {
    marketNotFound: "Market Not Found",
    marketNotFoundDesc:
      "The market you are looking for does not exist or has been removed.",
    loadFailed: "Failed to Load",
    loadFailedDesc: "Something went wrong while loading data.",
    noPositions: "No Positions",
    noPositionsDesc: "You have no positions yet. Place a bet to get started!",
    noProfile: "No Profile Data",
    noProfileDesc: "Place your first bet to create your profile!",
    emptyMarkets: "No Markets Found",
    emptyMarketsDesc:
      "There are no markets available right now. Check back soon!",
  },

  // Beta banner (testing-phase notice at top of every page)
  betaBanner: {
    label: "Beta",
    body: "BetMeBro is running on Solana devnet. Markets settle in test SOL only — please do not use real funds.",
    dismiss: "Dismiss beta notice",
  },

  // Breaking news sidebar
  breakingNews: {
    ariaLabel: "Breaking news",
    heading: "Breaking",
    dismiss: "Dismiss breaking news",
    loading: "Loading…",
    error: "Data source unavailable",
    empty: "No recent events",
    live: "LIVE",
    ft: "FT",
    canceled: "Canceled",
    justNow: "just now",
    minutesAgo: "{{count}}m ago",
    hoursAgo: "{{count}}h ago",
    daysAgo: "{{count}}d ago",
  },

  // Footer
  footer: {
    privacy: "Privacy",
    terms: "Terms of Use",
    copyright: "BetMeBro © {{year}}",
    // Trust-line that runs along the bottom of every page: states what
    // settles bets (oracle), the chain-as-truth invariant, and the
    // one-bet-per-market constraint that's enforced at the Bet PDA layer.
    trustLine: "Settled by detrust-oracle · On-chain · One bet per market",
  },

  // Legal pages
  legal: {
    lastUpdated: "Last updated",
    backHome: "Back to home",
    privacy: {
      title: "Privacy Policy",
      effectiveDate: "May 12, 2026",
      intro:
        'This Privacy Policy describes how BetMeBro ("we", "our", or "the Company") collects, uses, and discloses personal information when you access the website and prediction-market interface available at betmebro.fun (the "Site"). By using the Site, you consent to the practices described below. If you do not agree, please do not use the Site.',
      sections: {
        introduction: {
          title: "1. Introduction",
          body: "This Policy applies to the Site and to the services provided through it. It does not apply to third-party websites, smart contracts, oracles, wallets, or services that you may access via links from the Site, even when those third parties interoperate with BetMeBro. Continued use of the Site after a change in this Policy constitutes acceptance of the revised Policy.",
        },
        applicability: {
          title: "2. Applicability",
          body: '"Personal information" means information that identifies or is reasonably capable of identifying you as an individual. This Policy does not apply to aggregated or de-identified data that cannot reasonably be associated with you, nor to information that is already publicly available on the Solana blockchain (such as wallet addresses, transaction signatures, and on-chain position history).',
        },
        collection: {
          title: "3. Information Collection and Use",
          body: "We collect information you provide directly (e.g., display name, email if you opt in to notifications), information generated automatically when you browse (e.g., IP address, user agent, device identifiers, log files, referral URL), information derived from your activity on the Site (e.g., pages viewed, features used), financial information (e.g., your public wallet address and on-chain history), mobile device information when you use a mobile browser, and approximate geolocation derived from your IP address. We use this information to operate, secure, and improve the Site.",
        },
        minors: {
          title: "4. Children Under the Age of 18",
          body: "The Site is not directed to anyone under 18. We do not knowingly collect personal information from minors. If we learn that we have collected personal information from a person under 18, we will delete it. Parents or guardians who believe their child has provided personal information should contact us at the address below.",
        },
        cookies: {
          title: "5. Use of Cookies and Other Tracking Technologies",
          body: "We use cookies, local storage, and similar technologies to recognize returning visitors, remember preferences, maintain authenticated sessions, and analyze how the Site is used. Most browsers allow you to disable cookies or clear local storage; doing so may degrade or break parts of the Site, including the ability to remain signed in.",
        },
        use: {
          title: "6. Use of Your Information",
          body: "We use personal information to provide and maintain your account, authenticate wallet sessions, settle and display markets, prevent fraud and abuse, communicate about the Site, comply with applicable law and lawful requests, enforce our Terms, and improve product quality and performance.",
        },
        sharing: {
          title: "7. How We Share Personal Information with Other Parties",
          body: "We share personal information with service providers acting on our behalf (e.g., hosting, RPC, analytics, customer support, fraud detection), with business partners where you have authorized integration, with law enforcement and regulators where required by valid legal process, and with successor entities in connection with a merger, acquisition, or sale of assets. We do not sell personal information.",
        },
        thirdPartyLinks: {
          title: "8. Third-Party Links",
          body: "The Site may contain links to third-party websites, applications, or services that are not operated or controlled by us. We are not responsible for the privacy practices of those third parties, and we encourage you to read their privacy policies before providing them with personal information.",
        },
        analytics: {
          title: "9. Third-Party Analytics and Retargeters",
          body: "We may use third-party analytics services (such as web analytics providers) to understand how visitors use the Site. These services may set their own cookies and collect device and usage information subject to their own policies. You can opt out of many third-party analytics services through your browser settings or industry opt-out tools.",
        },
        preferences: {
          title: "10. Personal Communication Preferences",
          body: "You may access, update, or delete the off-chain personal information associated with your account from your account settings, and you may opt out of non-essential email communications at any time using the unsubscribe link or by contacting us. Transactional and legal notices may continue to be sent while you have an active account.",
        },
        transfer: {
          title: "11. Transfer of Data",
          body: "BetMeBro operates globally. By using the Site, you consent to the transfer, processing, and storage of your personal information in jurisdictions other than your own, including jurisdictions whose data-protection laws may differ from those of your country of residence.",
        },
        rights: {
          title: "12. Your Data Protection Rights",
          body: "Subject to applicable law, you may have the right to access the personal information we hold about you, correct inaccurate data, object to or restrict certain processing, request erasure, and receive a portable copy of your data. To exercise these rights, contact us at the address below. We will respond within the timeframe required by applicable law.",
        },
        deletion: {
          title: "13. Return/Deletion of Personal Data",
          body: "Upon a valid request, and subject to our legal and regulatory obligations, we will return or securely destroy the off-chain personal information we hold about you. Note that information already recorded on the Solana blockchain cannot be deleted by us.",
        },
        changes: {
          title: "14. Changes to Our Policy",
          body: "We may modify this Policy at any time. The revised Policy will be posted here with an updated effective date. Material changes will be highlighted in-product when appropriate. Your continued use of the Site after changes take effect constitutes acceptance of the revised Policy.",
        },
        jurisdiction: {
          title: "15. Jurisdiction",
          body: "The Site is operated from, and this Policy is governed by, the laws of the jurisdiction in which BetMeBro is established, without regard to conflict-of-laws principles.",
        },
        contact: {
          title: "16. Contact Us",
          body: "If you have questions about this Policy or wish to exercise any of the rights described above, contact us at privacy@betmebro.fun.",
        },
      },
    },
    terms: {
      title: "Terms of Use",
      effectiveDate: "May 12, 2026",
      intro:
        'These Terms of Use (the "Terms") constitute a binding agreement between you and BetMeBro (the "Company", "we", or "our") and govern your access to and use of the BetMeBro website and interface (the "Site"). By accessing or using the Site you agree to these Terms. If you do not agree, do not access or use the Site. You may not use the Site if you are located in, or a resident of, a prohibited jurisdiction or are otherwise subject to sanctions.',
      sections: {
        introduction: {
          title: "1. Introduction",
          body: "These Terms govern your use of the Site, including any features, tools, content, and applications made available through it. The Site is intended for users in jurisdictions where the activities offered through it are lawful. You are solely responsible for determining whether your access to or use of the Site is lawful under the laws of your jurisdiction, and for complying with any applicable laws.",
        },
        site: {
          title: "2. The Site and Features",
          body: "The Site provides an informational interface to a non-custodial prediction-market protocol that runs on the Solana blockchain. The Company does not operate an exchange, custody user funds, execute trades on your behalf, or control on-chain transactions. All trades and settlements occur peer-to-peer through smart contracts that you interact with directly using your own wallet. You alone are responsible for the transactions you sign and broadcast.",
        },
        modifications: {
          title: "3. Modifications",
          body: "We may modify these Terms, the Site, or any feature at any time, with or without notice. Where required by law we will provide reasonable notice of material changes. Your continued use of the Site after changes take effect constitutes your acceptance of the revised Terms.",
        },
        conduct: {
          title:
            "4. Your Responsibilities, Representations & Prohibited Conduct",
          body: "By using the Site you represent that: (a) you are of legal age in your jurisdiction; (b) you are not a person subject to economic sanctions and not located in a prohibited jurisdiction; (c) you have the legal capacity to enter these Terms; and (d) your use of the Site complies with applicable law. You agree not to use the Site to engage in market manipulation, wash trading, self-dealing, money laundering, fraud, or any other unlawful activity, and you agree not to interfere with the Site or its smart contracts, attempt to bypass access controls, or scrape the Site in violation of these Terms.",
        },
        ip: {
          title: "5. Intellectual Property Rights",
          body: "The Site, including its design, text, graphics, and non-open-source code, is owned by the Company or its licensors and is protected by intellectual-property laws. We grant you a limited, non-exclusive, non-transferable license to access and use the Site for personal, non-commercial purposes consistent with these Terms. Open-source components are licensed under their respective licenses. By submitting content to the Site (e.g., comments, market questions, profile data), you grant the Company a worldwide, royalty-free license to host, display, and use that content in connection with operating the Site.",
        },
        disclaimers: {
          title: "6. Disclaimers and Limitations of Liability",
          body: 'The Site is provided "as is" and "as available" without warranties of any kind, express or implied. We do not warrant that the Site will be uninterrupted, error-free, or secure, that oracle data will be accurate or timely, or that smart contracts are free of bugs. To the maximum extent permitted by law, the Company\'s aggregate liability arising out of or relating to the Site will not exceed US$100, and the Company will not be liable for indirect, incidental, special, consequential, punitive, or exemplary damages, or any loss of profits, data, or digital assets.',
        },
        law: {
          title: "7. Governing Law and Dispute Resolution",
          body: "These Terms are governed by the laws of the jurisdiction in which the Company is established, without regard to conflict-of-laws principles. Before initiating any formal proceeding, you agree to first contact us in good faith and attempt to resolve the dispute informally for at least thirty (30) days.",
        },
        arbitration: {
          title: "8. Arbitration Agreement & Class Action Waiver",
          body: "Any dispute that cannot be resolved informally shall be resolved exclusively by final and binding individual arbitration administered under recognized commercial-arbitration rules in the seat of the Company. You and the Company each waive any right to a jury trial and any right to participate in a class action, class arbitration, or representative proceeding. Where applicable law prohibits arbitration of a particular claim, that claim alone is excluded from this section.",
        },
        general: {
          title: "9. General Terms",
          body: "These Terms, together with the Privacy Policy, constitute the entire agreement between you and the Company regarding the Site. You may not assign these Terms; we may assign them to a successor in connection with a merger, acquisition, or sale of assets. A failure to enforce any provision is not a waiver. If any provision is found unenforceable, the remaining provisions remain in full force. Questions or notices should be sent to legal@betmebro.fun.",
        },
      },
    },
  },

  // Login modal
  loginModal: {
    reviewSignatureRequest: "Review Signature Request",
    reviewMessageBeforeSigning: "Review the message below before signing",
    signingMessage: "Signing Message",
    completeSignatureRequest:
      "Please complete the signature request in your wallet",
    success: "Success!",
    youAreSignedIn: "You are now signed in",
    error: "Error",
    somethingWentWrong: "Something went wrong",
    waitingForSignature: "Waiting for Signature",
    approveInWallet: "Please approve the signature request in your wallet...",
    preparingSignature: "Preparing signature request...",
    signingIn: "Signing In",
    preparingAuth: "Preparing authentication...",
    authenticationSuccessful: "Authentication Successful!",
    authenticationFailed: "Authentication Failed",
    errorOccurred: "An error occurred during authentication. Please try again.",
    cancel: "Cancel",
    signMessage: "Sign Message",
    checkWalletForSignature: "Check your wallet for the signature request...",
    close: "Close",
    tryAgain: "Try Again",
  },

  // World Cup 2026 event page
  worldCup: {
    title: "World Cup 2026",
    subtitle: "FIFA World Cup 2026 · Jun 11 – Jul 19 · 48 teams, 104 matches",
    tabs: { schedule: "Schedule", games: "Games", markets: "Markets" },
    fixturesError: "Could not load the match schedule.",
    marketsError: "Could not load World Cup markets.",
    retry: "Retry",
    group: "Group {{id}}",
    knockout: {
      r32: "Round of 32",
      r16: "Round of 16",
      qf: "Quarter-finals",
      sf: "Semi-finals",
      third: "Third place play-off",
      final: "Final",
    },
    tbd: "TBD",
    live: "LIVE",
    ft: "FT",
    postponed: "Postponed",
    canceled: "Canceled",
    outcome: { hw: "{{team}} win", dr: "Draw", aw: "{{team}} win" },
    createSlot: "Create",
    matchStarted: "Started",
    noGamesYet: "No matches with confirmed opponents yet.",
    noMarketsYet:
      "No World Cup markets yet — open the Games tab and create the first one.",
    marketsCount: "{{count}} markets",
    marketsCount_one: "{{count}} market",
    matchCount: "{{count}} matches",
    matchCount_one: "{{count}} match",
  },

  // Markets
  markets: {
    // Home page hero — split into two halves so the second can be colored
    // brand-accent (gold) inline. The pun on "Bet me" → BetMeBro is
    // deliberate; the Chinese translation captures the dare-tone rather
    // than the literal pun.
    heroEyebrow: "Live prediction markets",
    heroTitlePrefix: "Think you know?",
    heroTitleAccent: "Bet me.",
    featured: "Featured Markets",
    endingSoon: "Ending Soon",
    bettors: "Bettors",
    participants: "Participants",
    coveragePool: "Coverage Pool",
    placeBet: "Place Bet",
    placeABet: "Place a bet",
    insureBadge: "INSURE",
    hot: "HOT",
    allMarkets: "All Markets",
    pool: "Pool",
    // Detail page — odds-history chart card
    oddsHistory: "Odds history",
    ticks: "ticks",
    // Detail page — derived resolution-rules block label
    resolution: "Resolution",
    // Home page — replaces the LiveFeedTicker section heading
    recentActivity: "Recent activity",
    lastNBets: "Last {{count}} bets",
    closesIn: "Closes in",
    // Compact labels for the home-page PlatformStats <dl> row.
    // Single-word forms preferred so the eyebrow doesn't wrap.
    platformStats: {
      markets: "Markets",
      bettors: "Bettors",
      volume: "Volume",
      liveNow: "Live",
    },
    create: {
      eyebrow: "New market",
      title: "Create a market",
      walletRequiredDesc: "Please connect your wallet to create a market.",
      questionLabel: "Question",
      questionHintInsure:
        "Insure markets typically describe events with downside risk — flight delays, weather damage, protocol exploits, asset crashes. The question still resolves YES / NO.",
      modeTabAria: "Market mode",
      modeTab: {
        bet: "Bet",
        insure: "Insure",
      },
      modeTooltip: {
        bet: "Binary YES/NO outcome. Bettors stake on whether an event happens.",
        insure:
          "Buyers pay a premium for coverage; sellers stake to provide it.",
      },
      byMode: {
        bet: {
          subtitle:
            "Anyone can create a market. You pay a small creation fee + ~0.003 SOL Solana rent. Markets resolve via admin (v1) — your market lifecycle: open → event → resolved → users claim winnings.",
          questionPlaceholder: "Will BTC top $120,000 by 2026-06-15?",
          submitLabel: "Create market",
          closeLabel: "Betting closes",
          closeHint:
            "Leave empty to use the default (= event start). Override to close betting earlier; cannot be later than event start.",
        },
        insure: {
          subtitle:
            "Anyone can create a coverage market. Buyers pay a premium, sellers stake collateral. Resolves via admin (v1) — coverage lifecycle: open → event → resolved → buyers claim payout if triggered.",
          questionPlaceholder: "Will SFO flights be delayed >2h on 2026-06-15?",
          submitLabel: "Create coverage",
          closeLabel: "Coverage closes",
          closeHint:
            "Leave empty to use the default (= event start). Override to close premium purchases earlier; cannot be later than event start.",
        },
      },
      eventStart: "Event starts",
      eventEnd: "Event ends",
      timezoneHint:
        "Times below are interpreted in your local timezone ({{tz}} · {{offset}}). Double-check before submitting.",
      resolutionDeadline: "Resolution deadline",
      resolutionDeadlineHint:
        "Leave empty to use the default (= event end + 24h). Override to extend up to 7 days after event end (contract limit).",
      advancedTiming: "Advanced timing",
      advancedTimingHint: "Optional — defaults derived from event window",
      fees: {
        title: "Fees you'll pay",
        creation: "Creation fee:",
        rent: "Solana rent (Market + Escrow):",
        oracleRent: "DataRequest rent (oracle):",
      },
      submit: "Create market",
      submitting: "Creating…",
      wcMode: {
        eyebrow: "World Cup market",
        lockedHint:
          "Question and timing are derived from the selected match and cannot be edited.",
        matchLabel: "Match",
        outcomeLabel: "Outcome",
        kickoffLabel: "Kickoff (your local time)",
        invalidParams:
          "This match link is invalid or the match has already started — you can still create a free-form market below.",
      },
      errors: {
        questionMin: "Question must be at least 8 characters",
        questionMax: "Question must be 200 characters or fewer",
        noMarkerPrefix:
          "Questions cannot start with “[” — bracket prefixes are reserved for event markets.",
        required: "Required",
        mustBeFuture: "Must be in the future",
        mustBeFutureBuffer: "Must be at least 5 minutes in the future",
        afterEventStart: "Must be after event start",
        atOrBeforeEventStart: "Must be at or before event start",
        afterEventEnd: "Must be after event end",
        within7Days: "Cannot be more than 7 days after event end",
        walletNotConnected: "Wallet not connected.",
      },
      toasts: {
        success: "Market created!",
        pending:
          "Market submitted — finalising on-chain. It'll appear in the list shortly.",
        failure: "Could not create market",
      },
    },
  },

  // Market detail page — result-specific resolution strings. The generic
  // oracle trust vocabulary is reused from `betting.oracle.*`; only the
  // outcome / dispute / void copy lives here.
  marketDetail: {
    resolution: {
      outcomeLabel: "Outcome",
      resolvedAtLabel: "Resolved",
      yesWon: "YES won",
      noWon: "NO won",
      undisputed: "Undisputed",
      challenged: "Challenged through {{rounds}} round(s)",
      voided: "Voided · fully refunded",
      archived: "DataRequest archived on-chain",
    },
    settleReady: {
      waiting: "Event ended — awaiting oracle finalization",
      refresh: "Refresh oracle status",
      resolvedYes: "Oracle finalized: YES won",
      resolvedNo: "Oracle finalized: NO won",
      settleCta: "Settle Bet",
      settleHint:
        "Settling also writes the result on-chain and unlocks everyone else's claims.",
      waitingHolder:
        "Resolved on the oracle — waiting for any position holder to settle and sync the market.",
    },
  },

  // Betting
  betting: {
    yes: "YES",
    no: "NO",
    amount: "Bet Amount",
    placeYourBet: "Place Your Bet",
    notOnChainYet:
      "This market is still syncing on-chain — betting will open once the create-market transaction is confirmed.",
    alreadyBet:
      "You already have a bet on this market. Only one bet per market is allowed.",
    alreadyBetCta: "Already Bet",
    closesIn: "Closes in",
    insure: {
      buyTab: "Buy Protection",
      sellTab: "Provide Coverage",
      premiumAmount: "Premium Amount (you pay)",
      premium: "Premium (you pay)",
      estimatedPayout: "Estimated Payout (if triggered)",
      payoutRatio: "Payout Ratio",
      triggerCondition: "Trigger Condition",
      verificationSource: "Verification Source",
      howItWorks: "How it works:",
      howItWorksDesc:
        "Pay premium \u2192 Event triggers \u2192 Oracle verifies \u2192 Auto-payout to wallet (no claim needed)",
      confirmBuy: "Confirm Protection",
      payPrefix: "Pay",
      underwriteAmount: "Underwrite Amount (locked until resolved)",
      lockedFunds: "Locked Funds",
      expectedIncome: "Expected Premium Income",
      lockPeriod: "Lock Period",
      expectedYield: "Expected Yield",
      expectedYieldProb: "probability",
      maxRisk: "Max Risk",
      maxRiskProb: "probability",
      confirmSell: "Confirm Underwriting",
      lockPrefix: "Lock",
      riskWarningTitle: "Risk Warning",
      riskWarningDesc:
        "Funds will be locked until the coverage period ends. If the trigger condition is met, you will lose all underwritten funds.",
    },

    buy: "BUY",
    sell: "SELL",

    confirmModal: {
      title: "Confirm Your Bet",
      marketLabel: "Market",
      positionLabel: "Position",
      sideAtOdds: "{{label}} at {{odds}}%",
      amountLabel: "Amount",
      potentialPayoutLabel: "Potential Payout",
      payoutWithReturn: "{{payout}} SOL (+{{returnPct}}%)",
      submitButton: "Sign & Place Bet",
      cancelButton: "Cancel",
    },

    payoutCalc: {
      youPay: "You pay",
      potentialPayout: "Potential payout",
    },

    insureSelector: {
      buyTitle: "Buy Coverage",
      buySubtitle: "Get protected",
      sellTitle: "Sell Coverage",
      sellSubtitle: "Earn premium",
    },

    outcomeSelector: {
      bet: {
        yes: "YES",
        yesSub: "I think so",
        no: "NO",
        noSub: "I doubt it",
      },
      insure: {
        yes: "BUY",
        yesSub: "Get covered",
        no: "SELL",
        noSub: "Provide coverage",
      },
    },

    amountInput: {
      placeholder: "Custom amount",
      max: "Max: {{amount}} SOL",
    },

    social: {
      peopleBetting: "people betting",
      participants: "participants",
    },

    oracle: {
      settlementLabel: "Settlement: DeTrust Oracle",
      settlementSubtitle: "On-chain verified \u00b7 Multi-source consensus",
      verify: "Verify on-chain",
    },

    riskDisclaimer: {
      title: "Risk Disclaimer",
      body: "Prediction markets are speculative in nature. You may lose your entire bet amount. Only bet what you can afford to lose. Past performance does not guarantee future results. BetMeBro does not provide financial advice.",
    },

    insurePayout: {
      buyerLabel: "Your Coverage",
      buyerSubtitle: "Payout if event occurs",
      sellerLabel: "Premium Earned",
      sellerSubtitle: "Your upfront earnings",
      premiumRate: "Premium rate: {{value}}%",
      buyerCost: "Cost: {{amount}} SOL",
      sellerLiability: "Liability: {{amount}} SOL max",
    },
  },

  // Portfolio
  portfolio: {
    activeBets: "Active Bets",
    resolvedBets: "Resolved",
    claimable: "Claimable",
    noBets: "No bets yet",
    startBetting: "Start betting to see your positions here",
    claim: "Claim",
    refundable: "Refundable",
    refunded: "Refunded",
    reclaim: "Reclaim",
    recoverRent: "Recover Rent",
    expirable: "Expirable",
    profit: "Profit",
    wagered: "Wagered",
    payout: "Payout",
    potentialPayout: "Potential Payout",
    soClose: "So close!",
    won: "Won",
    lost: "Lost",
    active: "Active",
    showingResults_one: "{{count}} position",
    showingResults_other: "{{count}} positions",
    yourPositions: "Your Positions",
    unrealizedPnL: "Unrealized P&L",
    realizedPnL: "Realized P&L",
    pageTitle: "Portfolio",
    eyebrow: "Your bets",
    walletRequired: "Please connect your wallet to view your portfolio.",
    loadError: "Failed to load portfolio",
    unexpectedError: "An unexpected error occurred.",
    retry: "Retry",
    emptyTitle: "No positions yet",
    emptyDesc:
      "Browse markets and place your first bet to start building your portfolio.",
    browseMarkets: "Browse Markets",
    toasts: {
      reclaimSubmitted: "Submitted — your balance will update shortly",
      marketResolvedGoSettle:
        "This market is resolved — settle/claim instead of reclaiming",
    },
  },

  // Profile
  profile: {
    title: "My Profile",
    eyebrow: "Your record",
    signInPrompt: "Please sign in to view your profile.",
    stats: {
      totalBets: "Total Bets",
      winRate: "Win Rate",
      totalProfit: "Total Profit",
      bestWin: "Best Win",
    },
  },

  // Leaderboard
  leaderboard: {
    title: "Leaderboard",
    rank: "Rank",
    player: "Player",
    winRate: "Win Rate",
    profit: "Profit",
    totalBets: "Bets",
    noEntries: "No leaderboard data available yet.",
    topTraders: "Top Traders",
    anonymous: "Anonymous",
  },

  // Error state components
  errorStates: {
    somethingWentWrong: "Something Went Wrong",
    tryAgainLater: "Please try again later.",
    emptyHere: "Nothing Here Yet",
    checkBackLater: "Check back later for new content.",
    transactionFailed: "Transaction Failed",
    transactionFailedDesc:
      "Your transaction could not be processed. Please verify your balance and try again.",
    viewOnExplorer: "View on Explorer",
  },

  // Share
  share: {
    share: "Share",
    shareMarket: "Share this market",
    twitter: "Share on X",
    telegram: "Share on Telegram",
    copyLink: "Copy Link",
    more: "More options",
    copied: "Link copied to clipboard!",
    copyFailed: "Failed to copy link",
    shareFailed: "Share failed",
  },

  // Referral
  referral: {
    fetchError: "Failed to load referral data",
  },

  // Desktop gate
  desktopGate: {
    title: "Desktop Only",
    description:
      "BetMeBro is optimized for desktop browsers. Please visit on a larger screen for the best experience.",
  },
};
